import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { Component, OnInit, OnDestroy, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';
import { EmpresaStateService } from '../../services/empresa-state.service';
import { Subscription, interval } from 'rxjs';

export interface LogisticaLinha {
  nunota:      number;
  dtneg?:      string;
  prioridade:  string;
  imediato:    string;
  nomeCliente: string;
  vendedor:    string;
  prefixo?:    string;
  confNome?:   string;   // quem fez a conferência
  dataFimConf?: string;  // quando finalizou a conferência
  totalItens:  number;
}

@Component({
  selector: 'app-paineis-logistica',
  standalone: true,
  imports: [LoadingIndicatorComponent, CommonModule, FormsModule],
  templateUrl: './paineis-logistica.component.html',
  styleUrl: './paineis-logistica.component.css'
})
export class PaineisLogisticaComponent implements OnInit, OnDestroy {
  @ViewChild('painelWrapper') painelWrapper!: ElementRef;

  // ── Auto-refresh ───────────────────────────────────────────
  intervaloAtualizacao = 30;
  tempoRestante        = 30;
  private tickSub?: Subscription;

  // ── Paginação ──────────────────────────────────────────────
  paginaAtual      = 1;
  itensPorPagina   = 17;
  readonly opcoesPorPagina = [17, 20, 25, 50, 100];

  // ── Estado ─────────────────────────────────────────────────
  carregando    = false;
  pedidos: LogisticaLinha[] = [];
  erro          = '';
  telaCheiaAtiva = false;

  private sub?: Subscription;
  private _empresaSub?: Subscription;

  constructor(
    private gateway: SankhyaGatewayService,
    readonly empresaState: EmpresaStateService
  ) {}

  ngOnInit(): void {
    this.buscar();
    this.iniciarTimer();

    // Isolamento por empresa: filtrado pela empresa logada — recarrega ao
    // trocar a empresa selecionada.
    this._empresaSub = this.empresaState.empresaMudou.subscribe(() => {
      this.buscar();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this._empresaSub?.unsubscribe();
    this.pararTimer();
  }

  /** Nome de exibição da empresa atualmente selecionada (badge no cabeçalho). */
  get nomeEmpresaAtual(): string {
    const emp = this.empresaState.opcoes.find(e => e.codEmp === this.empresaState.selecionada);
    return emp?.nomeFant || emp?.razaoAbrev || '';
  }

  // ── Timer ──────────────────────────────────────────────────

  iniciarTimer(): void {
    this.pararTimer();
    if (this.intervaloAtualizacao > 0) {
      this.tempoRestante = this.intervaloAtualizacao;
      this.tickSub = interval(1000).subscribe(() => {
        this.tempoRestante--;
        if (this.tempoRestante <= 0) {
          this.buscar();
          this.tempoRestante = this.intervaloAtualizacao;
        }
      });
    }
  }

  pararTimer(): void {
    this.tickSub?.unsubscribe();
  }

  onIntervaloChange(): void {
    if (this.intervaloAtualizacao >= 1) {
      this.iniciarTimer();
    } else {
      this.pararTimer();
      this.tempoRestante = 0;
    }
  }

  // ── Tela Cheia ─────────────────────────────────────────────

  alternarTelaCheia(): void {
    const el = this.painelWrapper.nativeElement;
    if (!document.fullscreenElement) {
      el.requestFullscreen().then(() => { this.telaCheiaAtiva = true; }).catch(() => {});
    } else {
      document.exitFullscreen().then(() => { this.telaCheiaAtiva = false; });
    }
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange() {
    this.telaCheiaAtiva = !!document.fullscreenElement;
  }

  // ── Paginação ──────────────────────────────────────────────

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.pedidos.length / this.itensPorPagina));
  }

  get pedidosPagina(): LogisticaLinha[] {
    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    return this.pedidos.slice(inicio, inicio + this.itensPorPagina);
  }

  get paginas(): number[] {
    const total = this.totalPaginas;
    const atual = this.paginaAtual;
    const delta = 2;
    const range: number[] = [];
    for (let i = Math.max(1, atual - delta); i <= Math.min(total, atual + delta); i++) {
      range.push(i);
    }
    return range;
  }

  irParaPagina(p: number): void {
    if (p < 1 || p > this.totalPaginas) return;
    this.paginaAtual = p;
  }

  onItensPorPaginaChange(): void {
    this.paginaAtual = 1;
  }

  // ── Busca ──────────────────────────────────────────────────

  buscar(): void {
    this.carregando    = true;
    this.erro          = '';
    this.tempoRestante = this.intervaloAtualizacao;

    const codEmp = Number(this.empresaState.selecionada ?? 1);
    this.sub = this.gateway.buscarPedidosLogistica(codEmp).subscribe({
      next: (res: any) => {
        this.carregando = false;
        if (res?.sucesso && Array.isArray(res.pedidos)) {
          this.pedidos = res.pedidos;
        } else {
          this.erro = res?.erro || 'Erro desconhecido ao carregar pedidos de logística.';
        }
        this.paginaAtual = 1;
      },
      error: () => {
        this.carregando = false;
        this.erro = 'Falha de comunicação com o servidor. Tente novamente.';
      }
    });
  }
}
